import { useTranslator } from '@bluebeam/react-i18n';
import Tippy from '@tippyjs/react/headless';
import AscendingSort from 'components/Icons/AscendingSort';
import DescendingSort from 'components/Icons/DescendingSort';
import Sort from 'components/Icons/Sort';
import { Button } from 'components/lib';
import { useMapLayersManager } from 'providers/MapLayersManagerProvider';
import React, { useState } from 'react';
import { defaultValues } from 'records/MappedSheetQuery';

type Props = {
  id?: string;
  sortOptions: [string, string][];
  startingSortField: string;
  startingSortDirection: 'ascending' | 'descending';
};

const SortButton: React.FC<Props> = ({
  id,
  startingSortDirection,
  startingSortField,
  sortOptions,
}: Props) => {
  const [sortField, setSortField] = useState(startingSortField);
  const [sortDirection, setSortDirection] = useState(startingSortDirection);
  const mapLayersManager = useMapLayersManager();
  const { translate } = useTranslator(['mapView']);

  const handleSortDirectionChanged = () => {
    setSortDirection((prev) =>
      prev === 'ascending' ? 'descending' : 'ascending'
    );
  };

  const handleSortDisciplineChanged = (discipline: string) => {
    setSortField(discipline);
  };

  const handleApplySort = () => {
    const { mappedSheetQuery } = mapLayersManager;
    const newMappedSheetQuery = mappedSheetQuery
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);
    mapLayersManager.setMappedSheetQuery(newMappedSheetQuery);
  };

  const handleResetSort = () => {
    const { mappedSheetQuery } = mapLayersManager;
    const newMappedSheetQuery = mappedSheetQuery
      .set('sortField', defaultValues.sortField)
      .set('sortDirection', defaultValues.sortDirection);
    mapLayersManager.setMappedSheetQuery(newMappedSheetQuery);
    setSortField(defaultValues.sortField);
    setSortDirection(defaultValues.sortDirection);
  };

  const rowStyle = {
    paddingLeft: 24,
    paddingTop: 14,
    paddingBottom: 14,
    height: 44,
    alignItems: 'center',
  };

  const content = (attrs: any) => (
    <div
      {...attrs}
      className="bgd-white b-3"
      style={{
        minWidth: '256px',
        zIndex: 2,
      }}
    >
      <div className="column color-primary-dark">
        <div className="bb-2 w100 bold font-size-4 black-2" style={rowStyle}>
          {translate('mapView:sortButton.sortBy')}:
        </div>
        {sortOptions.map(([displayName, fieldName]) =>
          fieldName === sortField ? (
            <div
              key={`sheet-sorting-row-${fieldName}`}
              className="bb-3 w100 bold font-size-4 bgd-primary-blue-opacity-10 cursor-pointer row justify-between"
              onClick={handleSortDirectionChanged}
              style={rowStyle}
            >
              {displayName}
              <div className="mr3">
                {sortDirection === 'ascending' ? (
                  <AscendingSort width="20" height="20" />
                ) : (
                  <DescendingSort width="20" height="20" />
                )}
              </div>
            </div>
          ) : (
            <div
              key={`sheet-sorting-row-${fieldName}`}
              className="bb-3 w100 font-size-4 black-2 cursor-pointer"
              onClick={() => {
                handleSortDisciplineChanged(fieldName);
              }}
              style={rowStyle}
            >
              {displayName}
            </div>
          )
        )}
      </div>
      <div className="row justify-center p3">
        <Button
          id="layers-sort-reset-btn"
          className="mr2"
          onClick={handleResetSort}
          size="small"
          variant="secondary"
          disabled={
            sortField === defaultValues.sortField &&
            sortDirection === defaultValues.sortDirection
          }
        >
          {translate('mapView:sortButton.reset')}
        </Button>
        <Button
          id="layers-sort-apply-btn"
          onClick={handleApplySort}
          size="small"
          variant="primary"
          disabled={
            sortField === mapLayersManager.mappedSheetQuery.sortField &&
            sortDirection === mapLayersManager.mappedSheetQuery.sortDirection
          }
        >
          {translate('mapView:sortButton.apply')}
        </Button>
      </div>
    </div>
  );

  return (
    <Tippy
      arrow={false}
      render={(attrs) => content(attrs)}
      interactive
      placement="bottom"
      trigger="click"
    >
      <div className="hs2 ws2 flex justify-center items-center">
        <Button id={id} variant="tertiary" style={{ padding: '3px' }}>
          <Sort width="24" height="20" />
        </Button>
      </div>
    </Tippy>
  );
};

export default SortButton;
